<script setup lang="ts">
import { useTranslate } from "@/composables/useTranslate";
import { FocusImage } from "@/types";
import { reactive } from "vue";
import { ref, defineEmits, defineProps } from "vue";
import FocusModeDeleteModal from "./FocusModeDeleteModal.vue";

const { t } = useTranslate();

const props = defineProps<Props>();
type Props = {
  isModalOpen: boolean;
  image: FocusImage | null;
};

const emits = defineEmits<Emits>();
type Emits = {
  (event: "onClose"): void;
  (event: "onAddImageComment", comment: string, logId: number): void;
  (event: "onDeleteImageComment"): void;
};

const comment = ref(props.image?.comment);

const isDeleteModalOpen = ref(false);

const errors = reactive({
  comment: "",
});

const validate = () => {
  let isValid = [];
  errors.comment = "";

  if (!comment.value) {
    errors.comment = t("focus_mode_modal.error_comment_mandatory").value;
    isValid.push(false);
  } else {
    isValid.push(true);
    errors.comment = "";
  }

  return isValid.filter((item: boolean) => !item).length === 0;
};

const onSubmit = () => {
  if (!validate()) return;

  emits(
    "onAddImageComment",
    comment.value ? comment.value : "",
    props.image?.logId ? props.image.logId : 0
  );
};
</script>

<template>
  <div
    class="fixed top-0 right-0 bottom-0 left-0 z-[888] flex items-center justify-center w-full bg-black/80"
  >
    <Transition name="slide">
      <div
        v-if="isModalOpen"
        class="bg-white w-full max-w-[628px] py-4 px-5 rounded-primary-radius"
      >
        <!-- Header -->
        <div
          class="w-full flex items-center justify-between border-b border-b-black/10 pb-4 mb-4"
        >
          <h4
            class="text-primary-text-color font-semibold text-xl leading-8 tracking-[0.01rem]"
          >
            {{ t("focus_mode_modal.note") }}
          </h4>
          <button
            @click="emits('onClose')"
            class="flex items-center justify-center"
          >
            <img src="@/assets/close-icon-2.svg" alt="Close" />
          </button>
        </div>
        <!-- Input -->
        <input
          v-model="comment"
          type="text"
          class="p-3 border rounded-primary-radius outline-none w-full bg-[#F9F8F6] text-base leading-[26px] tracking-[0.01rem] font-normal text-black duration-300"
          :class="
            comment && comment?.length > 0
              ? 'border-primary-text-color'
              : 'border-black/10'
          "
          :placeholder="t('focus_mode_modal.note').value"
        />
        <p v-if="errors.comment" class="text-sm text-red-400 font-medium mt-1">
          {{ errors.comment }}
        </p>
        <!-- Footer -->
        <div class="mt-4 w-full flex items-center justify-end gap-4">
          <button
            @click="isDeleteModalOpen = true"
            class="flex items-center gap-2 p-3 rounded-primary-radius bg-[#F4B6B2]"
          >
            <img
              class="w-[18px] h-[18px]"
              src="@/assets/trash-icon-black.svg"
              alt="Delete"
            />
            <span
              class="text-primary-text-color text-base leading-5 tracking-[0.01rem] font-semibold"
              >{{ t("focus_mode_modal.delete_note") }}</span
            >
          </button>
          <div>
            <FocusModeDeleteModal
              :is-modal-open="isDeleteModalOpen"
              :text="t('focus_mode_modal.focus_delete_note_modal_message')"
              :is-delete-note="true"
              :is-loading="false"
              @on-comment-delete="emits('onDeleteImageComment')"
              @on-close="isDeleteModalOpen = false"
            />
          </div>
          <button
            @click="onSubmit"
            class="w-full max-w-[150px] p-3 rounded-primary-radius bg-primary-text-color text-white text-base leading-5 tracking-[0.01rem] font-semibold"
          >
            {{ t("global.save") }}
          </button>
        </div>
      </div>
    </Transition>
  </div>
</template>

<style scoped>
.slide-enter-active {
  transition: transform 0.5s ease;
}

.slide-leave-active {
  transition: transform 0.8s ease;
}

.slide-enter-from,
.slide-leave-to {
  transform: translateY(1000px);
}

.slide-enter-to,
.slide-leave-from {
  transform: translateY(0);
}
</style>
